import { LearnMoreSVG } from '@/components/svg/learn-more/learn-more'
import { CapacityInfo } from '@/components/v3/atoms/capacity-info/capacity-info'
import { CategoryTag } from '@/components/v3/atoms/category-tag/category-tag'
import { FeaturedBadge } from '@/components/v3/atoms/featured-badge/featured-badge'
import { IconLinkButton } from '@/components/v3/atoms/icon-link-button/icon-link-button'
import { LocationsList } from '@/components/v3/atoms/locations-list/locations-list'
import { OffersBadge } from '@/components/v3/atoms/offers-badge/offers-badge'
import { PoundIcon } from '@/components/svg/pound-icon/pound-icon'
import { CardImageScroller } from '@/components/v3/molecules/card-image-scroller/card-image-scroller'
import { FAVOURITE, SLIDER_CLASS_NAME } from '@/constants'
import { SupplierLocation } from '@/types/Supplier'
import { getCardHref } from '@/utils/getCardHref'
import cx from 'classnames'
import Link from 'next/link'
import { forwardRef } from 'react'
import { BookmarkIcon } from '../../../icons/bookmark-icon/bookmark-icon'
import { ReadMoreSVG } from '../../../svg/read-more/read-more'
import { Image } from '../../../v2/atoms/image/image'
import s from './card.module.css'

export type CardItem = {
  id: number | string
  title: string
  date?: string
  imageUrl?: string
  imageAlt?: string
  category?: string
  locations?: SupplierLocation[]
  path: string
}

type CardSize = 'Small' | 'Medium' | 'Large'

export type SupplierCardType = 'Supplier' | 'Venue'

export type CardType = 'Article' | SupplierCardType

const getFavoriteType = (type: CardType) => {
  switch (type) {
    case 'Article':
      return FAVOURITE.ARTICLE
    case 'Supplier':
      return FAVOURITE.SUPPLIER
    case 'Venue':
      return FAVOURITE.VENUE
    default:
      throw new Error(`Unknown card type: ${type}`)
  }
}

export type CardProps = {
  header: string
  date?: string
  category: string
  imageUrl: string
  imageAlt: string
  imageLoading?: 'lazy' | 'eager'
  itemId: number | string
  path: string
  type: CardType
  size?: CardSize
  adjustToSlider?: boolean
  reverseVertical?: boolean
  primaryColor?: string
  locations?: SupplierLocation[]
  instagramLink?: string
  websiteLink?: string
  headingLevel?: 'h2' | 'h3' | 'h4'
  isFeatured?: boolean
  hasActiveOffer?: boolean
  capacity?: string | null
  // Lower-bound price value (e.g. "500"), rendered as "£ from 500" above offers.
  price?: string | null
  galleryImages?: string[]
  // Selects the supplier/venue card's mobile layout. Both put the Featured badge
  // on the image and the bookmark next to the title. 'landscape' (default) is a
  // full-width 372x176 image on top; 'vertical' is a 124x149 image on the left
  // with the info on the right.
  mobileVariant?: CardMobileVariant
  // The nav mega menu's presentation of this card: no bookmark, and the header
  // held at the small (non-boosted) size to match the editorial card next to
  // it. Set only from the nav dropdown's card render.
  navMegaMenu?: boolean
}

export type CardMobileVariant = 'landscape' | 'vertical'

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      header,
      date,
      category,
      locations,
      imageUrl,
      imageAlt,
      imageLoading,
      itemId,
      type,
      path,
      size = 'Medium',
      adjustToSlider = false,
      reverseVertical = false,
      primaryColor = '#000',
      instagramLink,
      websiteLink,
      headingLevel: HeadingTag = 'h4',
      isFeatured = false,
      hasActiveOffer = false,
      capacity,
      price,
      galleryImages,
      mobileVariant = 'landscape',
      navMegaMenu = false,
    },
    ref
  ) => {
    const favType = getFavoriteType(type)
    const reverseVerticalArticle = type === 'Article' && reverseVertical
    const reverseVerticalSupplier = type === 'Supplier' && reverseVertical
    const hasArticleCategory = type === 'Article' && category
    const isSupplierOrVenue = type === 'Supplier' || type === 'Venue'
    // Image scroll applies only to listing cards (not the cross-link/offers sliders).
    const useScroller = isSupplierOrVenue && !adjustToSlider
    // The mobile layouts (landscape/vertical) only apply to listing cards that use
    // the scroller. Both share the same mobile treatment for the Featured badge
    // (overlaid on the image) and the bookmark (next to the title).
    const isVerticalMobile = useScroller && mobileVariant === 'vertical'
    const isLandscapeMobile = useScroller && mobileVariant === 'landscape'
    const scrollerImages = [imageUrl, ...(galleryImages ?? [])]
      .filter(Boolean)
      .slice(0, 5)

    const redirectPath = getCardHref(type, path)

    const renderHeaderInfo = (type: CardType) => {
      if (type === 'Article' && category) {
        return (
          <p className={s.category}>
            <span>{category}</span> / {date}
          </p>
        )
      }

      if (type === 'Supplier' || type === 'Venue') {
        return (
          <>
            {(category || isFeatured) && (
              <div className={s.categoryRow}>
                {category && <CategoryTag>{category}</CategoryTag>}
                {isFeatured &&
                  (useScroller ? (
                    <span className={s.featuredInlineHiddenMobile}>
                      <FeaturedBadge />
                    </span>
                  ) : (
                    <FeaturedBadge />
                  ))}
              </div>
            )}
            {(price || hasActiveOffer || (type === 'Venue' && capacity)) && (
              <div className={s.metaRow}>
                {price && (
                  <div className={s.priceRow}>
                    <span className={s.price}>
                      <PoundIcon className={s.priceIcon} />
                      <span>from £{price}</span>
                    </span>
                  </div>
                )}
                {hasActiveOffer && (
                  <div className={cx(s.badgeRow, s.offersRow)}>
                    <OffersBadge />
                  </div>
                )}
                {type === 'Venue' && capacity && (
                  <div className={cx(s.badgeRow, s.capacityRow)}>
                    <CapacityInfo
                      capacity={capacity}
                      className={s.capacityInfoNudge}
                    />
                  </div>
                )}
              </div>
            )}
            <div className={s.locationRow}>
              <LocationsList
                locations={
                  locations?.map((location) => location.location?.name) || []
                }
                iconProps={{
                  fill: primaryColor,
                  className: s.metaIconNudge,
                }}
                textClassName={s.locationWrapper}
              />
            </div>
          </>
        )
      }

      return null
    }

    const renderHoverSVG = (type: CardType) => {
      if (type === 'Article') {
        return <ReadMoreSVG className={s[`hoverSVG${size}`]} />
      }

      if (type === 'Supplier' || type === 'Venue') {
        return <LearnMoreSVG className={s[`hoverSVG${size}`]} />
      }

      return null
    }

    return (
      <div
        ref={ref}
        role="article"
        className={cx(
          { [SLIDER_CLASS_NAME]: adjustToSlider },
          { [s[`wrapperSlider${size}`]]: adjustToSlider },
          { [s.listingCardWrapper]: useScroller },
          s[`wrapper${size}`]
        )}
      >
        {!navMegaMenu && (
          <div
            className={cx(s.bookmark, {
              [s.bookmarkHiddenMobile]: useScroller,
            })}
          >
            <BookmarkIcon
              itemId={itemId}
              type={favType}
              primaryColor={primaryColor}
            />
          </div>
        )}

        <Link href={redirectPath} className={s.cardLink} aria-label={header} />

        <div
          className={cx(s.linkContainer, s[`container${size}`], {
            [s.reverseVerticalArticle]: reverseVerticalArticle,
            [s.reverseVerticalSupplier]: reverseVerticalSupplier,
            [s.stackedMobile]: useScroller,
            [s.verticalMobile]: isVerticalMobile,
            [s.landscapeMobile]: isLandscapeMobile,
          })}
        >
          <div
            className={cx(s.headerContainer, {
              [s.headerContainerCardMedium]: size === 'Medium',
              [s.supplierHeader]: isSupplierOrVenue,
            })}
            style={{
              color: primaryColor,
            }}
          >
            <HeadingTag
              className={cx(s.header, s[`header${size}`], {
                [s.supplierCardHeader]: reverseVerticalSupplier && !navMegaMenu,
                [s.hasNoCategory]: !hasArticleCategory,
                [s.headerNoBookmark]: navMegaMenu,
              })}
              style={{
                color: primaryColor,
              }}
            >
              {header}
            </HeadingTag>
            {renderHeaderInfo(type)}
            {useScroller && !navMegaMenu && (
              <div className={s.bookmarkHeader}>
                <BookmarkIcon
                  itemId={itemId}
                  type={favType}
                  primaryColor={primaryColor}
                />
              </div>
            )}
          </div>
          <div
            className={cx(s.imageWrapper, {
              [s.imageSliderWrapper]: adjustToSlider,
              [s.imageWrapperMediumCard]: size === 'Medium',
            })}
          >
            {!useScroller && (
              <div className={s.shadowHover}>{renderHoverSVG(type)}</div>
            )}

            {useScroller && isFeatured && (
              <span
                className={cx(s.featuredOverlay, {
                  [s.featuredOverlayVertical]: isVerticalMobile,
                })}
              >
                <FeaturedBadge />
              </span>
            )}

            {useScroller ? (
              <CardImageScroller
                images={scrollerImages}
                alt={imageAlt}
                href={redirectPath}
                heroLoading={imageLoading}
                mobileVariant={mobileVariant}
                width={size === 'Small' ? 300 : 600}
                height={size === 'Small' ? 600 : 900}
              />
            ) : (
              <Image
                alt={imageAlt}
                fill
                fit
                src={imageUrl}
                width={size === 'Small' ? 300 : 600}
                height={size === 'Small' ? 600 : 900}
                className={s.image}
                loading={imageLoading}
              />
            )}
            <div className={s.bottomSlot}>
              {websiteLink && (
                <IconLinkButton
                  href={websiteLink}
                  trackingType="website"
                  supplierPath={redirectPath}
                />
              )}
              {instagramLink && (
                <IconLinkButton
                  href={instagramLink}
                  trackingType="instagram"
                  supplierPath={redirectPath}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
)

Card.displayName = 'Card'

export default Card
